import { useCallback } from 'react'
import { IconButton, Skeleton } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Message } from '@weblite/ts-action/message'
import { CloseIcon } from '$common/icons/close'
import { PinListIcon } from '$common/icons/Pin-list'
import { PIN_VIEWER_HEIGHT } from '$comp/_app/components/chat/components/conf'
import { openRoomPins } from '$logic/router'
import { handleRoomPinMessage, usePinContentByIids } from '$logic/pin'
import { useOpenedRoomPermissions } from '$logic/opened-room'
import { showUnPinMessageConfirmModal } from '$comp/modals/confirmation-dialog'
import { PinContent } from './components'

interface Props {
  roomId: string
  iids: number[]
}
export function PinViewer({ iids, roomId }: Props) {
  const { loading, messages } = usePinContentByIids(roomId, iids)
  const { openedRoomPermissions } = useOpenedRoomPermissions()
  const onCancel = useCallback(() => {
    showUnPinMessageConfirmModal(() =>
      handleRoomPinMessage(roomId, iids[0], false),
    )
  }, [iids])
  const canPin = openedRoomPermissions?.pinMessage

  if (loading || (!loading && (!messages || !messages?.length)))
    return <PinViewerSkeleton />

  return (
    <Container>
      {canPin && messages?.length === 1 && (
        <IconButton sx={{ width: '32px', height: '32px' }} onClick={onCancel}>
          <CloseIcon sx={{ color: 'neutral500' }} />
        </IconButton>
      )}

      <PinContent messages={messages as Message[]} loading={!!loading} />

      {messages?.length && messages.length > 1 && (
        <IconButton
          onClick={() => {
            openRoomPins()
          }}
        >
          <PinListIcon />
        </IconButton>
      )}
    </Container>
  )
}

const Container = styled('div')`
  z-index: 2;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 5px 12px;
  background-color: #fff;
  height: ${PIN_VIEWER_HEIGHT}px;
`

const PinViewerSkeleton = () => {
  return (
    <Container>
      <Skeleton
        sx={{ margin: '8px' }}
        variant="circular"
        width="24px"
        height="24px"
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexGrow: '1',
          height: '38px',
          marginRight: '4px',
          gap: '8px',
        }}
      >
        <Skeleton variant="rectangular" width="0.125rem" height="2.125rem" />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <Skeleton variant="rounded" width="90px" height="14px" />
          <Skeleton variant="rounded" width="150px" height="16px" />
        </div>
      </div>

      <Skeleton
        sx={{ margin: '8px' }}
        variant="rounded"
        width="24px"
        height="24px"
      />
    </Container>
  )
}
